import { onDestroy } from "@/modules/_";
import gsap, { SplitText, reduced } from "@lib/gsap";

// Momentum-based inertia on a split title (inspired by Osmo's momentum hover).
// Roles are deliberately orthogonal so the layers read as intentional, not mush:
//   WORDS carry the block translation (x/y, no spin).
//   CHARS carry the rotation; their lateral motion comes from the magnetic ripple
//   below, not their own kick.
const WORD = { xy: 9, rot: 0, resistance: 750, clampXY: 110 };
const CHAR = { xy: 0, rot: 6, resistance: 850, clampXY: 200 };
const clampRot = gsap.utils.clamp(-16, 16);

// Magnetic ripple: hitting a char shoves nearby chars away from it, scaled by
// pointer speed and distance falloff. `wave` staggers the push by distance so it
// propagates outward as a wave instead of all reacting on the same frame.
// Off by default — it's the layer that read as "too much," since untouched
// letters move on their own. Flip RIPPLE back on to restore the wave.
const RIPPLE = false;
const NEIGHBOR = { radius: 90, push: 2.5, maxXY: 80, resistance: 750, wave: 0.04 };
const clampPush = gsap.utils.clamp(-NEIGHBOR.maxXY, NEIGHBOR.maxXY);

const canHover =
  typeof window !== "undefined" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

export default function (element: HTMLElement, _dataset: DOMStringMap) {
  const target = element.querySelector<HTMLElement>('[title-inertia="text"]');
  if (!target) return;

  // No animation on reduced-motion / touch — leave the heading as plain, selectable text.
  if (reduced || !canHover) return;

  // It's an interactive display title, not body copy: drop the I-beam and stop the
  // fragmented split spans from being selected (which looks broken + fights hover).
  target.style.cursor = "default";
  target.style.userSelect = "none";
  target.style.webkitUserSelect = "none";

  // Pointer velocity (px/frame), tracked globally and RAF-throttled. `primed`
  // skips the first sample so prevX/prevY aren't 0 on the opening move.
  let prevX = 0;
  let prevY = 0;
  let velX = 0;
  let velY = 0;
  let primed = false;
  let rafId: number | null = null;

  const onMove = (e: MouseEvent) => {
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      if (primed) {
        velX = e.clientX - prevX;
        velY = e.clientY - prevY;
      }
      prevX = e.clientX;
      prevY = e.clientY;
      primed = true;
      rafId = null;
    });
  };
  window.addEventListener("mousemove", onMove);

  const kick = (node: HTMLElement, e: MouseEvent, cfg: typeof WORD) => {
    const inertia: {
      resistance: number;
      x?: { velocity: number; end: number };
      y?: { velocity: number; end: number };
      rotation?: { velocity: number; end: number };
    } = { resistance: cfg.resistance };

    if (cfg.xy) {
      const clampXY = gsap.utils.clamp(-cfg.clampXY, cfg.clampXY);
      inertia.x = { velocity: clampXY(velX * cfg.xy), end: 0 };
      inertia.y = { velocity: clampXY(velY * cfg.xy), end: 0 };
    }

    if (cfg.rot) {
      const { left, top, width, height } = node.getBoundingClientRect();
      const offsetX = e.clientX - (left + width / 2);
      const offsetY = e.clientY - (top + height / 2);
      // Torque from the pointer's lever arm, normalised so spin tracks pointer
      // speed rather than how far off-centre it entered.
      const rawTorque = offsetX * velY - offsetY * velX;
      const lever = Math.hypot(offsetX, offsetY) || 1;
      inertia.rotation = { velocity: clampRot((rawTorque / lever) * cfg.rot), end: 0 };
    }

    // overwrite:"auto" only clears overlapping props, so a direct kick and the
    // magnetic ripple can layer on the same node instead of cancelling out.
    gsap.to(node, { inertia, overwrite: "auto" });
  };

  // Repel the hit char's neighbours, falling off with distance and pointer speed.
  type Center = { x: number; y: number };
  const pushNeighbors = (
    nodes: HTMLElement[],
    centers: Center[],
    i: number,
  ) => {
    const speed = Math.hypot(velX, velY);
    if (speed < 0.001) return;

    const ci = centers[i];
    for (let j = 0; j < nodes.length; j++) {
      if (j === i) continue;
      const dx = centers[j].x - ci.x;
      const dy = centers[j].y - ci.y;
      const dist = Math.hypot(dx, dy);
      if (dist === 0 || dist > NEIGHBOR.radius) continue;

      const falloff = 1 - dist / NEIGHBOR.radius;
      const mag = speed * NEIGHBOR.push * falloff * falloff;
      gsap.to(nodes[j], {
        inertia: {
          x: { velocity: clampPush((dx / dist) * mag), end: 0 },
          y: { velocity: clampPush((dy / dist) * mag), end: 0 },
          resistance: NEIGHBOR.resistance,
        },
        delay: (dist / NEIGHBOR.radius) * NEIGHBOR.wave, // farther = later → wave
        overwrite: "auto",
      });
    }
  };

  // Re-attaches on every (re)split so listeners always point at live nodes.
  let detach = () => {};

  const instance = SplitText.create(target, {
    type: "words, chars",
    wordsClass: "title-inertia__word",
    charsClass: "title-inertia__char",
    autoSplit: true,
    aria: "auto",
    onSplit: (self) => {
      detach();
      const offs: Array<() => void> = [];
      const chars = self.chars as HTMLElement[];

      // Resting centers relative to the heading — stable under scroll/transform,
      // computed once here since they only change on a re-split.
      const tRect = target.getBoundingClientRect();
      const centers: Center[] = chars.map((c) => {
        const r = c.getBoundingClientRect();
        return {
          x: r.left + r.width / 2 - tRect.left,
          y: r.top + r.height / 2 - tRect.top,
        };
      });

      chars.forEach((c, i) => {
        const handler = (e: MouseEvent) => {
          kick(c, e, CHAR);
          if (RIPPLE) pushNeighbors(chars, centers, i);
        };
        c.addEventListener("mouseenter", handler);
        offs.push(() => c.removeEventListener("mouseenter", handler));
      });

      (self.words as HTMLElement[]).forEach((w) => {
        const handler = (e: MouseEvent) => kick(w, e, WORD);
        w.addEventListener("mouseenter", handler);
        offs.push(() => w.removeEventListener("mouseenter", handler));
      });

      detach = () => {
        offs.forEach((off) => off());
        detach = () => {};
      };
    },
  });

  onDestroy(() => {
    window.removeEventListener("mousemove", onMove);
    if (rafId) cancelAnimationFrame(rafId);
    detach();
    gsap.killTweensOf([...instance.words, ...instance.chars]);
    instance.revert();
    target.style.cursor = "";
    target.style.userSelect = "";
    target.style.webkitUserSelect = "";
  });
}
